import * as React from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  RefreshCw,
  UserPlus,
  Users,
  X,
} from 'lucide-react'
import { ContentStatusBadge } from '../-components/ContentStatusBadge'
import { audienceSummary } from '../-components/audience-summary'
import type { LearningAssignmentRow } from '@/types/learning.types'
import { InfoHint } from '@/components/shared/InfoHint'
import { PageTitle } from '@/components/PageTitle'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { UserPicker } from '@/components/shared/UserPicker'
import { Label } from '@/components/ui/label'
import { ProgressRing } from '@/components/shared/ProgressRing'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useAssignVersion,
  useLearningAudiencePreview,
  useLearningVersion,
  useReinstateAssignment,
  useSetCourseRecurrence,
  useVersionAssignments,
  useWaiveAssignment,
} from '@/hooks/repositories/useLearningRepository'
import { useAllUsers } from '@/hooks/repositories/useUsersRepository'
import { requirePermission } from '@/lib/permissions/route-guards'
import { LEARNING_PERMISSION_KEYS } from '@/lib/permissions/learning-permissions'
import { formatDate } from '@/lib/formatters'

export const Route = createFileRoute('/_app/learning/admin/$versionId/assign')({
  beforeLoad: requirePermission(LEARNING_PERMISSION_KEYS.assign),
  component: RouteComponent,
})

/**
 * How often a course comes round again (sc-1276).
 *
 * Labelled by what people actually say — "quarterly", "twice a year" — rather
 * than only in months. Somebody setting a compliance schedule is thinking in
 * those terms, not in multiples of a month.
 */
const RECURRENCE_OPTIONS = [
  { value: 'off', label: 'Does not repeat' },
  { value: '3', label: 'Quarterly — every 3 months' },
  { value: '6', label: 'Twice a year — every 6 months' },
  { value: '12', label: 'Yearly — every 12 months' },
  { value: '24', label: 'Every 2 years' },
] as const

function initials(first: string, last: string) {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase()
}

function StatusBadge({
  row,
  repeats,
}: {
  row: LearningAssignmentRow
  /** Whether the course is on a repeat schedule — changes what an excusal means. */
  repeats: boolean
}) {
  if (row.status === 'COMPLETED') {
    return (
      <Badge
        variant="outline"
        className="border-emerald-500/30 bg-emerald-500/10 text-[10px] text-emerald-700 dark:text-emerald-400"
      >
        Complete
      </Badge>
    )
  }
  if (row.status === 'WAIVED') {
    // "Excused" means exactly what it sounds like: it is off their list and
    // they do not have to do it. The catalog filters WAIVED out of every
    // learner query. The one caveat is only true when the course repeats, so
    // it is only shown then — as a blanket warning it read as though an
    // excusal did not really excuse anything.
    return (
      <span className="inline-flex items-center gap-1">
        <Badge variant="outline" className="text-[10px]">
          Excused
        </Badge>
        <InfoHint label="What excused means">
          Off their list — they do not have to do this course.
          {repeats && ' They will get it again at the next repeat.'}
        </InfoHint>
      </span>
    )
  }
  if (row.status === 'SUPERSEDED') {
    return (
      <Badge variant="outline" className="text-[10px]">
        Replaced by a newer version
      </Badge>
    )
  }
  if (row.isOverdue) {
    return (
      <Badge
        variant="outline"
        className="border-destructive/40 bg-destructive/10 text-[10px] text-destructive"
      >
        Overdue
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="text-[10px]">
      {row.lessonsCompleted > 0 ? 'In progress' : 'Not started'}
    </Badge>
  )
}

/**
 * Assign a course and see who has done it (sc-1245).
 *
 * Its own page rather than a tab on the editor: authoring and chasing people
 * are separate jobs done at separate times, and the roster is a table that
 * wants room. Gated on `learning.assign`, which someone can hold without
 * `learning.author` — the person who chases completion is often not the person
 * who wrote the course.
 */
function RouteComponent() {
  const { versionId } = Route.useParams()
  const { data: version } = useLearningVersion(versionId)
  const { data: roster, isPending } = useVersionAssignments(versionId)
  const { data: users } = useAllUsers()

  const assign = useAssignVersion(versionId)
  // How many people the audience rule resolves to right now — the same
  // resolver the assignment sweep runs, so the preview and the eventual
  // assignment cannot disagree.
  const { data: audiencePreview } = useLearningAudiencePreview(versionId)
  const waive = useWaiveAssignment(versionId)
  const reinstate = useReinstateAssignment(versionId)
  const setRecurrence = useSetCourseRecurrence(versionId)

  const [selected, setSelected] = React.useState<Set<string>>(new Set())
  const [dueAt, setDueAt] = React.useState('')

  const assignedUserIds = React.useMemo(
    () => new Set((roster ?? []).map((row) => row.user.id)),
    [roster],
  )

  // Anyone already on the roster is excluded from the picker rather than shown
  // ticked: assigning them again is a no-op, so offering it would be offering a
  // button that does nothing. That includes the excused — putting one of them
  // back is Reinstate on their own row, not a second trip through this picker,
  // which would have been the no-op.

  const isPublished = version?.status === 'PUBLISHED'

  function toggle(userId: string) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }

  const completedCount = (roster ?? []).filter(
    (row) => row.status === 'COMPLETED',
  ).length
  const overdueCount = (roster ?? []).filter((row) => row.isOverdue).length

  return (
    <div className="py-4">
      <PageTitle title="Learning — People" />

      <Button asChild variant="ghost" size="sm" className="mb-3 -ml-2">
        <Link to="/learning/admin/$versionId" params={{ versionId }}>
          <ArrowLeft className="mr-1 size-4" aria-hidden />
          Back to the course
        </Link>
      </Button>

      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
            {version?.title ?? 'Course'}
          </h1>
          {version && <ContentStatusBadge status={version.status} />}
        </div>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Who this course is for, and how they are getting on.
        </p>
      </div>

      {!isPublished && (
        <Card className="mb-6 border-amber-500/40 bg-amber-500/5">
          <CardContent className="py-4 text-sm">
            <p className="font-medium">This version is not published</p>
            <p className="mt-1 text-muted-foreground">
              Only a published version can be assigned — otherwise people would
              be given unfinished work they cannot open.
            </p>
          </CardContent>
        </Card>
      )}

      {/* ── Who it is for, and how often ───────────────────────────────
          Both shown ALWAYS. They used to sit inside the rollup, which only
          rendered once somebody was already assigned — so a draft, or a
          published course nobody had started, showed neither. That is what
          made this page look like it was about something else entirely. */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2">
        <Card>
          <CardContent className="py-5">
            <Label className="mb-2 flex items-center gap-1.5 text-xs">
              <Users className="size-3" aria-hidden />
              Who this is for
              {/* Three caveats used to sit under this card as prose. They are
                  all still true and all still worth knowing — they just are
                  not worth reading every time you open the page. */}
              <InfoHint label="How the audience works">
                This is the rule that picks who gets the course. The list below
                is who actually has it. A published version cannot be edited, so
                changing the rule means starting a new draft.
              </InfoHint>
            </Label>
            <p className="text-sm font-medium">
              {version ? audienceSummary(version) : '—'}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {audiencePreview?.count != null &&
                `${audiencePreview.count} ${audiencePreview.count === 1 ? 'person' : 'people'} match right now. `}
              <Link
                to="/learning/admin/$versionId"
                params={{ versionId }}
                className="text-primary hover:underline"
              >
                Start a draft
              </Link>{' '}
              to change it.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="py-5">
            <Label className="mb-2 flex items-center gap-1.5 text-xs">
              <RefreshCw className="size-3" aria-hidden />
              Repeat this course
            </Label>
            <Select
              value={
                version?.course.recurrence?.isActive
                  ? String(version.course.recurrence.intervalMonths)
                  : 'off'
              }
              onValueChange={(value) =>
                version &&
                setRecurrence.mutate({
                  courseId: version.courseId,
                  intervalMonths: value === 'off' ? null : Number(value),
                })
              }
            >
              <SelectTrigger aria-label="Repeat interval">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECURRENCE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* "Who is it recurring for" had no answer on screen. Audited the
                sweep: it calls assignToAudience with the next cycle, so it
                goes to whoever matches the rule AT THAT MOMENT — not to
                whoever happened to have it last time. */}
            <p className="mt-2 text-xs text-muted-foreground">
              {version?.course.recurrence?.isActive ? (
                <>
                  Goes to{' '}
                  <span className="text-foreground">
                    {audienceSummary(version).toLowerCase()}
                  </span>{' '}
                  each time.
                </>
              ) : (
                'For a policy people must re-acknowledge — an annual handbook, say.'
              )}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ── Rollup ─────────────────────────────────────────────────────── */}
      {roster && roster.length > 0 && (
        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          <Card>
            <CardContent className="flex items-center gap-4 py-5">
              <ProgressRing
                percent={Math.round((completedCount / roster.length) * 100)}
                size={56}
                strokeWidth={5}
              />
              <div>
                <p className="text-xl font-bold tabular-nums">
                  {completedCount}
                  <span className="text-sm font-normal text-muted-foreground">
                    {' '}
                    / {roster.length}
                  </span>
                </p>
                <p className="text-sm text-muted-foreground">completed</p>
              </div>
            </CardContent>
          </Card>

          <Card className={overdueCount > 0 ? 'border-destructive/40' : ''}>
            <CardContent className="py-5">
              <p className="text-xl font-bold tabular-nums">{overdueCount}</p>
              <p className="text-sm text-muted-foreground">overdue</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* ── Roster ───────────────────────────────────────────────────── */}
        <div className="lg:col-span-2">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Users className="size-4" aria-hidden />
            Who has it now
            <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground tabular-nums">
              {roster?.length ?? 0}
            </span>
          </h2>

          {isPending ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          ) : !roster || roster.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                Nobody has this course yet. The audience above is applied when a
                major revision publishes — until then this list stays empty. To
                give it to one person now, pick them on the right.
              </CardContent>
            </Card>
          ) : (
            <ul className="space-y-2">
              {roster.map((row) => (
                <li
                  key={row.id}
                  className="flex items-center gap-3 rounded-lg border bg-card p-3"
                >
                  <Avatar className="size-9 shrink-0">
                    <AvatarFallback className="text-xs">
                      {initials(row.user.firstName, row.user.lastName)}
                    </AvatarFallback>
                  </Avatar>

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {row.user.firstName} {row.user.lastName}
                    </p>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <StatusBadge
                        row={row}
                        repeats={version?.course.recurrence?.isActive ?? false}
                      />
                      <span>
                        {row.lessonsCompleted} of {row.lessonCount}
                      </span>
                      {row.dueAt && <span>Due {formatDate(row.dueAt)}</span>}
                    </div>
                  </div>

                  {row.status === 'COMPLETED' ? (
                    <CheckCircle2
                      className="size-5 shrink-0 text-emerald-600 dark:text-emerald-400"
                      aria-label="Completed"
                    />
                  ) : (
                    <div className="flex shrink-0 items-center gap-2">
                      <ProgressRing
                        percent={row.percentComplete}
                        size={36}
                        strokeWidth={4}
                      >
                        <span className="text-[10px] font-semibold tabular-nums">
                          {row.percentComplete}
                        </span>
                      </ProgressRing>
                      {/* Excusing used to be a one-way door: a waived row
                          still holds the unique (person, version, cycle) slot,
                          so re-assigning them was a silent no-op, and the
                          picker hid anyone already on the roster. Undoing it
                          belongs on their row, where the mistake is visible. */}
                      {row.status === 'WAIVED' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => reinstate.mutate(row.id)}
                          disabled={reinstate.isPending}
                          title="Put it back on their list. Any progress they already made is kept."
                        >
                          Reinstate
                        </Button>
                      ) : (
                        row.status !== 'SUPERSEDED' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => waive.mutate(row.id)}
                            disabled={waive.isPending}
                          >
                            Excuse
                          </Button>
                        )
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── Add people ───────────────────────────────────────────────── */}
        <div>
          <Card className="lg:sticky lg:top-4">
            <CardContent className="space-y-4 py-5">
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                <UserPlus className="size-4" aria-hidden />
                Add people
              </h2>

              <UserPicker
                multiple
                staffOnly
                value={[...selected]}
                onChange={(ids) => setSelected(new Set(ids))}
                excludeUserIds={[...assignedUserIds]}
                placeholder="Search staff…"
              />

              {/* Chips rather than only a count on the trigger: when you are
                  about to assign work to named people, you should be able to
                  see exactly who without reopening the list. */}
              {selected.size > 0 && (
                <div className="flex flex-wrap gap-1">
                  {[...selected].map((userId) => {
                    const person = (users ?? []).find(
                      (user) => user.id === userId,
                    )
                    return (
                      <Badge
                        key={userId}
                        variant="secondary"
                        className="gap-1 py-1 pr-1 pl-2 text-xs font-normal"
                      >
                        {person
                          ? `${person.firstName} ${person.lastName}`
                          : 'Selected'}
                        <button
                          type="button"
                          onClick={() => toggle(userId)}
                          className="rounded-sm hover:bg-muted"
                          aria-label={
                            person
                              ? `Remove ${person.firstName} ${person.lastName}`
                              : 'Remove'
                          }
                        >
                          <X className="size-3" aria-hidden />
                        </button>
                      </Badge>
                    )
                  })}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="assign-due">Due date</Label>
                <Input
                  id="assign-due"
                  type="date"
                  value={dueAt}
                  onChange={(event) => setDueAt(event.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty to use the course default.
                </p>
              </div>

              <Button
                className="w-full"
                disabled={
                  selected.size === 0 || assign.isPending || !isPublished
                }
                onClick={() =>
                  assign.mutate(
                    {
                      versionId,
                      userIds: [...selected],
                      dueAt: dueAt ? new Date(dueAt).toISOString() : undefined,
                    },
                    { onSuccess: () => setSelected(new Set()) },
                  )
                }
              >
                {assign.isPending && (
                  <Loader2 className="mr-1 size-4 animate-spin" aria-hidden />
                )}
                Assign{selected.size > 0 ? ` ${selected.size}` : ''}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
